/**
 * Lusty Chic Fit — Your Lusty Fit results page controller (Phase 6).
 * Namespace: window.LustyChicFit (reserved in Phase 1 / CLAUDE.md).
 * Storage key: lustychic_fit_profile (reserved in Phase 1, written by assets/lc-fit-quiz.js).
 *
 * Reads the saved profile and the product data payload embedded by
 * sections/lc-fit-results.liquid, asks LustyChicFit.Engine (assets/lc-fit-engine.js) to
 * score and rank the eligible pool, then moves each qualifying product's pre-rendered
 * card (Impulse's own snippets/product-grid-item.liquid markup, rendered server-side) out
 * of the hidden pool and into the Best Matches / More Styles For You grid, adding only the
 * match-percentage and match-reason info that the engine produced. No product HTML is
 * built client-side — price, image and availability stay exactly as Liquid rendered them.
 *
 * UX correction (2026-09-20): leg_shape is now an array (unlimited multi-select on the
 * quiz). hasAnyAnswer() and renderProfileSummary() below were updated to treat it as an
 * array (with transparent support for a legacy single-string value) instead of a single
 * truthy value — an empty array is not itself "answered", and every selected fit now
 * renders as its own chip.
 */
window.LustyChicFit = window.LustyChicFit || {};

(function (ns) {
  'use strict';

  var STORAGE_KEY = 'lustychic_fit_profile';

  var RISE_DISPLAY = { Low: 'Low Rise', Mid: 'Mid Rise', High: 'High Rise' };
  var LEG_DISPLAY = { Skinny: 'Skinny', Slim: 'Slim', Straight: 'Straight', Flare: 'Flare', Wide: 'Wide Leg' };

  function safeGetProfile() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function hasAnyAnswer(profile) {
    if (!profile) return false;
    return !!(
      profile.body_shape ||
      profile.body_shape_unsure ||
      profile.age_range ||
      profile.height ||
      profile.rise ||
      (Array.isArray(profile.leg_shape) ? profile.leg_shape.length : profile.leg_shape) ||
      (profile.fit_goals && profile.fit_goals.length)
    );
  }

  function readProductData(root) {
    var node = root.querySelector('[data-lc-fit-product-data]');
    if (!node) return [];
    try {
      var data = JSON.parse(node.textContent);
      return data instanceof Array ? data : [];
    } catch (e) {
      return [];
    }
  }

  function buildInfoBlock(percent, reasons) {
    var info = document.createElement('div');
    info.className = 'lc-fit-result-card__info';

    var badge = document.createElement('p');
    badge.className = 'lc-fit-result-card__badge';
    badge.textContent = percent + '% Fit Match';
    badge.setAttribute('role', 'text');
    info.appendChild(badge);

    if (reasons.length) {
      var label = document.createElement('p');
      label.className = 'lc-fit-result-card__reasons-label';
      label.textContent = 'Why it suits your preferences';
      info.appendChild(label);

      var list = document.createElement('ul');
      list.className = 'lc-fit-result-card__reasons';
      for (var i = 0; i < reasons.length; i++) {
        var item = document.createElement('li');
        var icon = document.createElement('span');
        icon.className = 'lc-fit-result-card__check';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '✓';
        item.appendChild(icon);
        item.appendChild(document.createTextNode(reasons[i]));
        list.appendChild(item);
      }
      info.appendChild(list);
    }

    return info;
  }

  // Phase 6C — renders as a row of .lc-fit-profile-tag chips (visual change only; the
  // underlying profile data and the parts collected below are unchanged from Phase 6).
  function renderProfileSummary(root, profile) {
    var container = root.querySelector('[data-lc-fit-profile]');
    if (!container) return;
    var list = container.querySelector('[data-lc-fit-profile-list]');
    var parts = [];

    if (profile.body_shape_unsure) {
      parts.push('Shape not sure yet');
    } else if (profile.body_shape) {
      parts.push(profile.body_shape);
    }
    if (profile.rise) parts.push(RISE_DISPLAY[profile.rise] || profile.rise);
    var legFits = Array.isArray(profile.leg_shape) ? profile.leg_shape : profile.leg_shape ? [profile.leg_shape] : [];
    for (var k = 0; k < legFits.length; k++) parts.push(LEG_DISPLAY[legFits[k]] || legFits[k]);
    if (profile.fit_goals && profile.fit_goals.length) {
      for (var i = 0; i < profile.fit_goals.length; i++) parts.push(profile.fit_goals[i]);
    }

    if (list) {
      list.innerHTML = '';
      for (var j = 0; j < parts.length; j++) {
        var tag = document.createElement('span');
        tag.className = 'lc-fit-profile-tag';
        tag.textContent = parts[j];
        list.appendChild(tag);
      }
    }
    container.hidden = false;
  }

  function placeResults(root, ranked) {
    var pool = root.querySelector('[data-lc-fit-card-pool]');
    var bestSection = root.querySelector('[data-lc-fit-best-section]');
    var moreSection = root.querySelector('[data-lc-fit-more-section]');
    var bestGrid = root.querySelector('[data-lc-fit-best]');
    var moreGrid = root.querySelector('[data-lc-fit-more]');
    if (!pool || !bestGrid || !moreGrid) return;

    var best = ranked.slice(0, 3);
    var more = ranked.slice(3);

    function place(entry, grid) {
      var card = pool.querySelector('[data-product-id="' + entry.product.id + '"]');
      if (!card) return;
      var infoSlot = card.querySelector('[data-lc-fit-info]');
      var info = buildInfoBlock(entry.score.displayedScore, entry.reasons);
      if (infoSlot && infoSlot.parentNode) {
        infoSlot.parentNode.replaceChild(info, infoSlot);
      } else {
        card.insertBefore(info, card.firstChild);
      }
      card.hidden = false;
      grid.appendChild(card);
    }

    for (var i = 0; i < best.length; i++) place(best[i], bestGrid);
    for (var j = 0; j < more.length; j++) place(more[j], moreGrid);

    if (best.length && bestSection) bestSection.hidden = false;
    if (more.length && moreSection) moreSection.hidden = false;
  }

  function showFallback(root, key) {
    var el = root.querySelector('[data-lc-fit-' + key + ']');
    if (el) el.hidden = false;
  }

  function isDebugRequested() {
    try {
      return new URLSearchParams(window.location.search).get('fitdebug') === '1';
    } catch (e) {
      return false;
    }
  }

  // Phase 6b Task 6 — internal QA view only. Never rendered unless ?fitdebug=1 is
  // explicitly present in the URL; a normal storefront visit never sees or builds this.
  // Shows, per eligible product: raw compatibility, available/max points, completeness,
  // confidenceFactor, the final displayedScore, whether it qualifies for personalised
  // results, and the per-category earned/available breakdown.
  function renderDebugPanel(root, profile, products) {
    var Engine = ns.Engine;
    var panel = document.createElement('div');
    panel.setAttribute('data-lc-fit-debug', '');
    panel.style.cssText = 'margin:32px auto;max-width:1200px;overflow-x:auto;font:12px/1.4 monospace;';

    var heading = document.createElement('p');
    heading.textContent = 'LC Fit debug (?fitdebug=1) — internal QA only, not shown to customers';
    heading.style.cssText = 'font-weight:bold;margin:0 0 8px;';
    panel.appendChild(heading);

    var table = document.createElement('table');
    table.style.cssText = 'border-collapse:collapse;width:100%;';
    var head = document.createElement('tr');
    var cols = ['Product', 'Available', 'Raw%', 'Avail/Max', 'Completeness', 'ConfFactor', 'Displayed', 'Qualifies', 'BodyShape', 'FitGoals', 'Rise', 'LegFit', 'Stretch'];
    for (var c = 0; c < cols.length; c++) {
      var th = document.createElement('th');
      th.textContent = cols[c];
      th.style.cssText = 'border:1px solid #ccc;padding:4px 6px;text-align:left;background:#f5f5f5;';
      head.appendChild(th);
    }
    table.appendChild(head);

    function categoryCell(cat) {
      if (!cat) return '—';
      return cat.earned.toFixed(1) + '/' + cat.available + (cat.matched ? ' ✓' : '');
    }

    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var score = Engine.scoreProduct(profile, product);
      var row = document.createElement('tr');
      var cells = [
        product.title,
        product.available ? 'yes' : 'no',
        score.rawCompatibility + '%',
        score.availablePoints + '/' + score.maxPossiblePoints,
        Math.round(score.completeness * 1000) / 10 + '%',
        score.confidenceFactor.toFixed(3),
        score.displayedScore + '%',
        score.qualifies ? 'YES' : 'no',
        categoryCell(score.categories.bodyShape),
        categoryCell(score.categories.fitGoals),
        categoryCell(score.categories.rise),
        categoryCell(score.categories.legFit),
        categoryCell(score.categories.stretch)
      ];
      for (var j = 0; j < cells.length; j++) {
        var td = document.createElement('td');
        td.textContent = cells[j];
        td.style.cssText = 'border:1px solid #ddd;padding:4px 6px;' + (j === 7 && score.qualifies ? 'font-weight:bold;' : '');
        row.appendChild(td);
      }
      table.appendChild(row);
    }

    panel.appendChild(table);
    root.appendChild(panel);
  }

  function init(root) {
    var Engine = ns.Engine;
    if (!Engine) return;

    var profile = safeGetProfile();
    var products = readProductData(root);

    if (isDebugRequested()) {
      renderDebugPanel(root, profile || {}, products);
    }

    if (!hasAnyAnswer(profile)) {
      showFallback(root, 'no-profile');
      return;
    }

    var ranked = Engine.rankProducts(profile, products);

    if (!ranked.length) {
      renderProfileSummary(root, profile);
      showFallback(root, 'no-matches');
      return;
    }

    renderProfileSummary(root, profile);
    placeResults(root, ranked);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('[data-lc-fit-results]');
    if (root) init(root);
  });
})(window.LustyChicFit);
